/* eslint-disable @typescript-eslint/no-explicit-any */
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { Card, Table } from 'antd';
import { useCallback } from 'react';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { COLUMNS_WIDTH } from '@/utils/table';
import WorkOrderItemQuantity from '../../../Items/WorkOrderItemList/WorkOrderItemQuantity';
import WorkOrderItemTypeTag from '../../../Items/WorkOrderItemList/WorkOrderItemTypeTag';
import { useIntl } from 'react-intl';
import { useWorkOrderComplementBenefitList } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { WorkOrderComplementBenefit } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';

interface Props {
  visible: boolean;
}

export default function BenefitsInfoAlert({ visible }: Props) {
  const intl = useIntl();

  const {
    workorder: { data: workorder },
    complement: { data: currComplement },
  } = useWorkorderDetailContext();

  const { data: complementsBenefits } = useWorkOrderComplementBenefitList(
    { workOrderId: workorder?.id, showOnlyUsedBenefits: false },
    visible,
  );

  const tableData = useCallback(
    (benefitItems: WorkOrderComplementBenefit[]) =>
      benefitItems?.reduce((acc, curr) => {
        if (
          curr.benefitServiceCode &&
          acc.some(
            (item) => item.benefitServiceCode === curr.benefitServiceCode,
          )
        ) {
          return acc.map((item) => {
            if (item.benefitServiceCode === curr.benefitServiceCode) {
              return {
                ...item,
                children: [
                  ...(item.children || []),
                  {
                    ...curr,
                    disableChangePart: true,
                  },
                ],
              };
            }
            return item;
          });
        }

        if (curr.benefitServiceCode) {
          return [
            ...acc,
            {
              ...curr,
              description: String(curr.benefitName),
              sku: '',
              type: WorkOrderItemType.Benefit,
              disableEdit: true,
              disableChangePart: true,
              children: [
                {
                  ...curr,
                  disableChangePart: true,
                },
              ],
            },
          ];
        }

        return [...acc, curr];
      }, [] as any[]) || [],
    [],
  );

  const renderTable = (data: any[]) => (
    <Table
      dataSource={tableData(data)}
      pagination={false}
      scroll={{ x: true }}
      expandable={{ defaultExpandAllRows: true }}
      columns={[
        {
          dataIndex: 'type',
          title: intl.formatMessage({
            id: 'workOrders.domain.items.type',
          }),
          width: 200,
          ellipsis: true,
          render: (_, item) => <WorkOrderItemTypeTag workOrderItem={item} />,
        },
        {
          dataIndex: 'description',
          title: intl.formatMessage({
            id: 'workOrders.domain.items.description',
          }),
          render: (value, item) => (
            <EllipsisHoverToggle
              style={
                item.type === WorkOrderItemType.Kit ||
                item.type === WorkOrderItemType.Benefit
                  ? { fontWeight: 'bold' }
                  : {}
              }
            >
              {value}
            </EllipsisHoverToggle>
          ),
        },
        {
          dataIndex: 'quantity',
          width: COLUMNS_WIDTH.action1,
          ellipsis: true,
          title: intl.formatMessage({
            id: 'workOrders.domain.items.quantityTime',
          }),
          align: 'right',
          render: (_, item) => <WorkOrderItemQuantity item={item} />,
        },
      ]}
    />
  );

  if (!complementsBenefits?.length) return null;

  return (
    <Card style={{ borderRadius: 8 }}>
      <p style={{ fontWeight: '600', fontSize: '1.1rem' }}>
        Os benefícios vinculados aos complementos terão a baixa de utilização
        efetuada no momento do fechamento.
      </p>

      {complementsBenefits.map((complement) => (
        <Card
          key={complement.complementId}
          title={
            <Group gap={10}>
              <strong>{complement.complementIdentifier}</strong>
              {complement.complementId === currComplement?.id && (
                <StatusTag label="Complemento atual" color="blue" />
              )}
            </Group>
          }
          bodyStyle={{ padding: 10 }}
          headStyle={{ paddingInline: 10 }}
          style={{ marginTop: 16, borderRadius: 8 }}
        >
          {renderTable(complement.items)}
        </Card>
      ))}
    </Card>
  );
}
